import { useEffect, useState } from "react";
import { Box, TablePagination, alpha, type Theme } from "@mui/material";
import type { DiaColuna } from "@/components/escala/espelho";
import { EspelhoCabecalho, type ColunaTotal } from "@/components/escala/EspelhoCabecalho";
import { CelulaNome } from "@/components/escala/EspelhoLinha";
import { LARGURA_TOTAL, celulaBase, faixa, fixaDireita, fixaEsquerda } from "@/components/escala/espelhoEstilos";

export interface LinhaGrade {
  id: string;
  titulo: string;
  subtitulo?: string;
}

export interface CelulaGrade {
  conteudo: React.ReactNode;
  titulo?: string;
}

export interface TotalGrade<L> extends ColunaTotal {
  valor: (linha: L) => React.ReactNode;
}

export interface RodapeGrade {
  titulo: string;
  celula: (coluna: DiaColuna) => React.ReactNode;
  totais?: React.ReactNode[];
}

interface RelatorioGradeMensalProps<L extends LinhaGrade> {
  colunas: DiaColuna[];
  linhas: L[];
  rotulo: string;
  celula: (linha: L, coluna: DiaColuna) => CelulaGrade | null;
  totais: TotalGrade<L>[];
  rodape?: RodapeGrade;
  avatar?: boolean;
}

const hoverLinha = {
  "&:hover td": {
    boxShadow: (theme: Theme) => `inset 0 0 0 999px ${alpha(theme.palette.text.primary, 0.04)}`,
  },
};

const celulaTotal = (indice: number, quantidade: number) =>
  ({
    ...celulaBase,
    ...fixaDireita((quantidade - 1 - indice) * LARGURA_TOTAL),
    fontWeight: 700,
    fontSize: 13,
    borderLeft: indice === 0 ? "1px solid" : "none",
    borderLeftColor: "divider",
  }) as const;

// Grade só de leitura no formato do espelho (linha × dia do mês), paginada no cliente; o quadro vem de quem usa.
export function RelatorioGradeMensal<L extends LinhaGrade>({
  colunas,
  linhas,
  rotulo,
  celula,
  totais,
  rodape,
  avatar = true,
}: RelatorioGradeMensalProps<L>) {
  const [pagina, setPagina] = useState(0);
  const [porPagina, setPorPagina] = useState(25);

  // Filtro novo devolve pra primeira página.
  useEffect(() => setPagina(0), [linhas]);

  const visiveis = linhas.slice(pagina * porPagina, pagina * porPagina + porPagina);

  return (
    <Box>
      <Box sx={{ overflow: "auto", maxHeight: "70vh" }}>
        <Box
          component="table"
          aria-label={`Grade por ${rotulo.toLowerCase()}`}
          sx={{ borderCollapse: "separate", borderSpacing: 0, fontSize: 12, width: "max-content", minWidth: "100%" }}
        >
          <EspelhoCabecalho colunas={colunas} rotulo={rotulo} totais={totais} />
          <tbody>
            {visiveis.map((linha) => (
              <Box component="tr" key={linha.id} sx={hoverLinha}>
                <CelulaNome titulo={linha.titulo} subtitulo={linha.subtitulo} avatar={avatar} />
                {colunas.map((c) => {
                  const conteudo = celula(linha, c);
                  return (
                    <Box component="td" key={c.dia} title={conteudo?.titulo} sx={[celulaBase, faixa(c)]}>
                      {conteudo?.conteudo}
                    </Box>
                  );
                })}
                {totais.map((total, i) => (
                  <Box component="td" key={total.rotulo} sx={celulaTotal(i, totais.length)}>
                    {total.valor(linha)}
                  </Box>
                ))}
              </Box>
            ))}
          </tbody>
          {rodape && (
            <tfoot>
              <Box component="tr" sx={{ "& td": { position: "sticky", bottom: 0, bgcolor: "background.paper", borderTop: "2px solid", borderTopColor: "divider" } }}>
                <Box component="td" sx={{ ...celulaBase, ...fixaEsquerda, zIndex: 3, textAlign: "left", pl: 2.5, fontWeight: 700, fontSize: 12, color: "text.secondary" }}>
                  {rodape.titulo}
                </Box>
                {colunas.map((c) => (
                  <Box component="td" key={c.dia} sx={[celulaBase, faixa(c), { fontWeight: 700, fontSize: 12 }]}>
                    {rodape.celula(c)}
                  </Box>
                ))}
                {totais.map((total, i) => (
                  <Box component="td" key={total.rotulo} sx={{ ...celulaTotal(i, totais.length), zIndex: 3 }}>
                    {rodape.totais?.[i]}
                  </Box>
                ))}
              </Box>
            </tfoot>
          )}
        </Box>
      </Box>
      <TablePagination
        component="div"
        count={linhas.length}
        page={pagina}
        rowsPerPage={porPagina}
        rowsPerPageOptions={[10, 25, 50, 100]}
        labelRowsPerPage="Linhas por página"
        onPageChange={(_, p) => setPagina(p)}
        onRowsPerPageChange={(e) => {
          setPorPagina(parseInt(e.target.value, 10));
          setPagina(0);
        }}
      />
    </Box>
  );
}

